import { useCallback } from "react";

import { INotification } from "../../store/types";
import { useAnswerHook } from "./use-answer-hook";
import { useCharacteristicHook } from "./use-characteristic-hook";
import { useClassificationHook } from "./use-classification-hook";
import { useCommentHandle } from "./use-comment-hook";
import { useChrunchbaseHook } from "./use-crunchbase-hook";
import { useDataroomHook } from "./use-dataroom-hook";
import { useDocumentHook } from "./use-document-hook";
import { useEmailHook } from "./use-email.hook";
import { useFileHook } from "./use-file-hook";
import { useFolderHook } from "./use-folder-hook";
import { useFormHook } from "./use-form-hook";
import { useNoteHook } from "./use-note-hook";
import { useQuestionHook } from "./use-question-hook";
import { useReplyHook } from "./use-reply-hook";
import { useSurveyCommentHook } from "./use-survey-comment-hook";
import { useSurveyReplyHook } from "./use-survey-reply-hook";
import { useTaggerHook } from "./use-tagger-hook";
import { useTaskHook } from "./use-task-hook";

export type NotificationHandlerType = (notification: INotification) => Promise<void>;

export function useClickHandler(): NotificationHandlerType {
  const handlerAnswer: NotificationHandlerType = useAnswerHook();

  const handleComment: NotificationHandlerType = useCommentHandle();

  const handleFile: NotificationHandlerType = useFileHook();

  const replyHandle: NotificationHandlerType = useReplyHook();

  const formHandle: NotificationHandlerType = useFormHook();

  const folderHandle: NotificationHandlerType = useFolderHook();

  const questionHandle: NotificationHandlerType = useQuestionHook();

  const handleSurveyComment: NotificationHandlerType = useSurveyCommentHook();

  const handleSurveyReply: NotificationHandlerType = useSurveyReplyHook();

  const handleEmail: NotificationHandlerType = useEmailHook();

  const handleNote: NotificationHandlerType = useNoteHook();

  const taskHandler: NotificationHandlerType = useTaskHook();

  const taggerHandler: NotificationHandlerType = useTaggerHook();

  const characteristicHandler: NotificationHandlerType = useCharacteristicHook();

  const chrunchbaseHandler: NotificationHandlerType = useChrunchbaseHook();

  const dataroomHandler: NotificationHandlerType = useDataroomHook();

  const documentHandler: NotificationHandlerType = useDocumentHook();

  const classificationHandler: NotificationHandlerType = useClassificationHook();

  return useCallback(
    async (notification: INotification) => {
      if (notification.action === "answer") {
        handlerAnswer(notification);
        return;
      }
      if (notification.action === "comment") {
        if (!notification.components.question_id) {
          handleComment(notification);
          return;
        }
        handleSurveyComment(notification);
        return;
      }
      if (notification.action === "file") {
        handleFile(notification);
        return;
      }
      if (notification.action === "reply") {
        if (!notification.components.question_id) {
          replyHandle(notification);
          return;
        }
        handleSurveyReply(notification);
        return;
      }
      if (notification.action === "form") {
        formHandle(notification);
        return;
      }
      if (notification.action === "answer_questions") {
        formHandle(notification);
        return;
      }
      if (notification.action === "folder") {
        folderHandle(notification);
        return;
      }
      if (notification.action === "question") {
        questionHandle(notification);
        return;
      }
      if (notification.action === "incoming_email") {
        handleEmail(notification);
        return;
      }

      if (notification.action === "note") {
        handleNote(notification);
        return;
      }
      if (notification.action === "task") {
        taskHandler(notification);
        return;
      }
      if (notification.action === "crunchbase") {
        chrunchbaseHandler(notification);
        return;
      }

      if (notification.action === "pdf_tagger") {
        taggerHandler(notification);
        return;
      }

      if (notification.action === "characteristic") {
        characteristicHandler(notification);
        return;
      }

      if (notification.action === "parse_pdf_file") {
        handleFile(notification);
        return;
      }

      if (notification.action === "dataroom") {
        dataroomHandler(notification);
        return;
      }

      if (notification.action === "document_answer_questions" || notification.action === "document") {
        documentHandler(notification);
        return;
      }
      if (notification.action === "classification") {
        classificationHandler(notification);
        return;
      }
    },
    [
      handlerAnswer,
      handleFile,
      formHandle,
      folderHandle,
      taskHandler,
      documentHandler,
      handleNote,
      handleEmail,
      handleSurveyComment,
      handleComment,
      replyHandle,
      handleSurveyReply,
      chrunchbaseHandler,
      dataroomHandler,
      characteristicHandler,
      taggerHandler,
      questionHandle,
      classificationHandler
    ]
  );
}
